import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Check, Repeat, Weight } from "lucide-react";
import { VideoPlayer } from "@/components/ui/video-player";

interface SetProgressProps {
  currentSetIndex: number;
  setData: {
    reps: number;
    weight: number;
  };
  exerciseId: string;
  completedSets: { [key: string]: boolean[] };
  totalSets: number;
  onSetComplete: (exerciseId: string, setIndex: number) => void;
  videoUrl?: string;
}

export const SetProgress = ({
  currentSetIndex,
  setData,
  exerciseId,
  completedSets,
  totalSets,
  onSetComplete,
  videoUrl,
}: SetProgressProps) => {
  console.log('🎯 SetProgress - Displaying enhanced set data:', {
    exerciseId,
    currentSetIndex,
    setData,
    totalSets,
    videoUrl,
    isEnhancedData: setData && typeof setData.reps === 'number' && typeof setData.weight === 'number'
  });

  const completedCount = completedSets[exerciseId]?.filter(Boolean).length || 0;
  const isCurrentSetCompleted = completedSets[exerciseId]?.[currentSetIndex];

  return (
    <div className="w-full space-y-6">
      {videoUrl && (
        <div className="w-full aspect-video rounded-2xl overflow-hidden bg-black/30 border border-white/10 shadow-lg">
          <VideoPlayer src={videoUrl} />
        </div>
      )}

      <div className="text-center space-y-6">
        {/* Serie counter section - más prominente */}
        <div className="flex flex-col items-center justify-center gap-2">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Serie</span>
          <div className="relative">
            <span className="text-8xl font-black bg-gradient-to-br from-primary via-primary/80 to-primary/50 bg-clip-text text-transparent drop-shadow-lg">
              {currentSetIndex + 1}
            </span>
            {/* Glow effect */}
            <div className="absolute inset-0 text-8xl font-black text-primary/20 blur-xl -z-10">
              {currentSetIndex + 1}
            </div>
          </div>
          <span className="text-sm text-muted-foreground">de {totalSets}</span>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent w-full" />

        {/* Reps and weight section with enhanced data */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-5 rounded-2xl bg-white/5 dark:bg-white/[0.03] backdrop-blur-sm border border-white/10 space-y-2 transition-all hover:bg-white/10">
            <div className="flex items-center justify-center gap-2">
              <Repeat className="h-4 w-4 text-primary/70" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Repeticiones</span>
            </div>
            <div className="text-4xl font-bold text-foreground">
              {setData?.reps || 0}
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-white/5 dark:bg-white/[0.03] backdrop-blur-sm border border-white/10 space-y-2 transition-all hover:bg-white/10">
            <div className="flex items-center justify-center gap-2">
              <Weight className="h-4 w-4 text-primary/70" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Peso</span>
            </div>
            <div className="text-4xl font-bold text-foreground">
              {setData?.weight || 0}
              <span className="text-lg text-muted-foreground ml-1">kg</span>
            </div>
          </div>
        </div>

        {/* Complete button */}
        <Button
          className={cn(
            "w-full py-8 text-lg font-semibold transition-all duration-300 rounded-2xl shadow-xl",
            isCurrentSetCompleted 
              ? "bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 shadow-green-500/10"
              : "bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground shadow-primary/25 hover:shadow-primary/40 hover:scale-[1.02] active:scale-[0.98]"
          )}
          onClick={() => onSetComplete(exerciseId, currentSetIndex)}
        >
          {isCurrentSetCompleted ? (
            <div className="flex items-center justify-center gap-2 animate-fade-in">
              <Check className="h-6 w-6" />
              <span>Completada</span>
            </div>
          ) : (
            "Marcar como completado"
          )}
        </Button>

        {/* Progress bar section */}
        <div className="space-y-3">
          <div className="flex gap-1.5 justify-between">
            {Array.from({ length: totalSets }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  "h-2.5 flex-1 rounded-full transition-all duration-500",
                  index === currentSetIndex
                    ? "bg-primary shadow-lg shadow-primary/50"
                    : completedSets[exerciseId]?.[index]
                    ? "bg-primary/50"
                    : "bg-white/10"
                )}
              />
            ))}
          </div>
          <p className="text-sm text-muted-foreground text-center font-medium">
            {completedCount} de {totalSets} series completadas
          </p>
        </div>
      </div>
    </div>
  );
};
